import React, { useEffect, useRef, useState } from "react";
import { PaperClipOutlined, PlayCircleOutlined } from "@ant-design/icons";
import { Button, Form, Select, Upload, message } from "antd";
import { useTranslation } from "react-i18next";

import { mediaLibraryActions } from "@fitness-app/app-store";
import {
  isVideoAttachment,
  MAX_BULK_MESSAGE_ATTACHMENTS,
  type UploadedMessageAttachment,
} from "@fitness-app/data-models/entities/ChatAttachment";
import { MediaLibraryResourceType, type MediaLibraryItem } from "@fitness-app/data-models/entities/MediaLibrary";

import FileItem from "~/components/Upload/FileItem";
import { UploadFileStatus, type FileData } from "~/components/Upload/types";
import { UploadFilesProvider, useUploadFilesContext } from "~/components/Upload/UploadProvider";
import VideoPlayer from "~/components/VideoPlayer/VideoPlayer";
import { getBulkMessageUploadConfig } from "~/modules/Chat/uploadConfig";
import { createMediaLibraryVideoOptions } from "~/modules/TrainingPrograms/tabs/ExercisesDatabase/ExerciseForm/types";
import { useAppDispatch, useAppSelector } from "~/store/initializeStore";

/** Form fields the component owns: `attachments` (uploads) and `mediaLibraryVideoId`. */
export interface MessageAttachmentsFormValues {
  attachments?: UploadedMessageAttachment[];
  mediaLibraryVideoId?: string | null;
}

/**
 * Splits saved attachments back into the two fields: uploads cannot be videos (the uploader
 * excludes them), so a video attachment is always the one picked from the media library.
 */
export const toMessageAttachmentsFormValues = (
  attachments: UploadedMessageAttachment[] | null | undefined,
): MessageAttachmentsFormValues => ({
  attachments: (attachments ?? []).filter((file) => !isVideoAttachment(file)),
  mediaLibraryVideoId: (attachments ?? []).find(isVideoAttachment)?.uid ?? null,
});

const toAttachment = (file: FileData & { url: string }): UploadedMessageAttachment => ({
  uid: file.uid,
  originalName: file.originalName,
  shortName: file.shortName,
  size: file.size,
  contentType: file.contentType,
  extension: file.extension,
  url: file.url,
  thumbUrl: file.thumbUrl || null,
  duration: file.duration || null,
});

const toFileData = (file: UploadedMessageAttachment): FileData => ({
  ...file,
  progress: null,
  status: UploadFileStatus.SUCCESS,
  objectPath: "",
});

interface AttachmentsInputProps {
  value?: UploadedMessageAttachment[];
  onChange?: (value: UploadedMessageAttachment[]) => void;
  onUploadingChange: (uploading: boolean) => void;
}

const AttachmentsInputContent = ({ value = [], onChange, onUploadingChange }: AttachmentsInputProps) => {
  const { t } = useTranslation("dashboard");
  const { files, onUploadFiles, removeFile } = useUploadFilesContext();

  // Finished uploads move into the field value; the provider list only shows what is in flight.
  useEffect(() => {
    const finished = files.filter(
      (file): file is FileData & { url: string } =>
        file.status === UploadFileStatus.SUCCESS && Boolean(file.url) && !value.some((item) => item.uid === file.uid),
    );

    if (finished.length) {
      onChange?.([...value, ...finished.map(toAttachment)]);
    }

    onUploadingChange(files.some((file) => file.status === UploadFileStatus.UPLOADING));
  }, [files]);

  const visibleFiles = [...value.map(toFileData), ...files.filter((file) => file.status !== UploadFileStatus.SUCCESS)];

  // A file uploaded in this form is deleted from storage. A saved one is only unlinked:
  // tasks already scheduled for clients still point at it.
  const handleRemove = (file: FileData) => {
    const uploadedHere = files.find((item) => item.uid === file.uid);

    if (uploadedHere) {
      removeFile(uploadedHere);
    }

    if (value.some((item) => item.uid === file.uid)) {
      onChange?.(value.filter((item) => item.uid !== file.uid));
    }
  };

  return (
    <>
      {/* antd only picks the files; the upload context sends them to storage and validates them. */}
      <Upload
        multiple
        fileList={[]}
        showUploadList={false}
        beforeUpload={(file, fileList) => {
          // Called once per picked file; the whole selection is handled on the first call.
          if (file !== fileList[0]) {
            return false;
          }

          // The provider checks each file against the list as it was before the selection,
          // so a batch is checked here as a whole.
          const inFlight = files.filter((item) => item.status === UploadFileStatus.UPLOADING).length;

          if (value.length + inFlight + fileList.length > MAX_BULK_MESSAGE_ATTACHMENTS) {
            void message.error(t("automatedMessageForm.tooManyAttachments", { max: MAX_BULK_MESSAGE_ATTACHMENTS }));
            return false;
          }

          onUploadFiles(fileList);
          return false;
        }}
      >
        <Button icon={<PaperClipOutlined />}>{t("automatedMessageForm.addAttachments")}</Button>
      </Upload>
      {visibleFiles.length ? (
        <ul className="mt-3 flex list-none flex-wrap gap-4 p-0">
          {visibleFiles.map((file) => (
            <FileItem key={file.uid} file={file} onRemove={handleRemove} />
          ))}
        </ul>
      ) : null}
    </>
  );
};

const AttachmentsInput = (props: AttachmentsInputProps) => {
  const { t } = useTranslation("dashboard");
  const userId = useAppSelector((store) => store.user.data?.id);

  // The upload folder is named after the user; without one the path would be `bulk//...`.
  if (!userId) {
    return (
      <Button icon={<PaperClipOutlined />} disabled>
        {t("automatedMessageForm.addAttachments")}
      </Button>
    );
  }

  return (
    <UploadFilesProvider {...getBulkMessageUploadConfig(userId)}>
      <AttachmentsInputContent {...props} />
    </UploadFilesProvider>
  );
};

/**
 * Attachments of a chat message composed in a form (TRE-2306): uploaded files and a video
 * from the marketing media library, the one campaigns use.
 */
export const MessageAttachmentsFormItems = () => {
  const { t } = useTranslation(["dashboard", "common"]);
  const dispatch = useAppDispatch();
  const form = Form.useFormInstance();
  const uploadingRef = useRef(false);
  const selectedVideoId = Form.useWatch("mediaLibraryVideoId", form);
  const mediaLibrary = useAppSelector((store) => store.mediaLibrary.list);
  const [previewVideo, setPreviewVideo] = useState<MediaLibraryItem | null>(null);
  // A message can only carry a video Bunny already encoded to MP4.
  const videoOptions = createMediaLibraryVideoOptions(mediaLibrary.filter((item) => item.data.mp4Url));

  useEffect(() => {
    dispatch(mediaLibraryActions.subscribeToMediaLibrary(MediaLibraryResourceType.Marketing));
  }, [dispatch]);

  return (
    <>
      <Form.Item
        name="attachments"
        label={t("automatedMessageForm.attachments")}
        rules={[
          {
            validator: () =>
              uploadingRef.current
                ? Promise.reject(new Error(t("automatedMessageForm.waitForAttachments")))
                : Promise.resolve(),
          },
        ]}
      >
        <AttachmentsInput
          onUploadingChange={(uploading) => {
            uploadingRef.current = uploading;
          }}
        />
      </Form.Item>

      <Form.Item
        name="mediaLibraryVideoId"
        label={t("automatedMessageForm.videoFromLibrary")}
        help={
          selectedVideoId ? (
            <Button
              icon={<PlayCircleOutlined />}
              onClick={() => setPreviewVideo(mediaLibrary.find((item) => item.id === selectedVideoId) || null)}
              className="my-2"
            >
              {t("common:button.preview")}
            </Button>
          ) : null
        }
      >
        <Select
          showSearch
          allowClear
          options={videoOptions}
          filterOption={(inputValue, option) => option?.label.toLowerCase().indexOf(inputValue.toLowerCase()) !== -1}
          placeholder={t("automatedMessageForm.videoFromLibraryPlaceholder")}
        />
      </Form.Item>

      <VideoPlayer
        videoUrl={previewVideo?.data.hlsUrl || previewVideo?.data.mp4Url || null}
        visible={!!previewVideo}
        onClose={() => setPreviewVideo(null)}
      />
    </>
  );
};
